import { useMemo } from "react";
import { useSelector } from "src/store";
import { RootState } from "src/store";
import { UserType } from "src/types";
import { isSal125SubscriptionProduct } from "src/types/askSalah";

const TRIAL_DURATION_DAYS = parseInt(
  process.env.NEXT_PUBLIC_TRIAL_DURATION_DAYS || "7",
  10
);
const MAX_ITEMS_ALLOWED_DURING_TRIAL = parseInt(
  process.env.NEXT_PUBLIC_MAX_ITEMS_ALLOWED_DURING_TRIAL || "1",
  10
);

const parseSubscriptionDate = (value: string | Date | null | undefined): Date | null => {
  if (value == null) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const isSubscriptionInActiveWindow = (
  startDate: string | Date | null | undefined,
  endDate: string | Date | null | undefined,
  now: Date = new Date()
): boolean => {
  const start = parseSubscriptionDate(startDate);
  const end = parseSubscriptionDate(endDate);
  if (!start || !end) return false;
  return start <= now && end >= now;
};

/** Active SAL125 subscription row from auth user payload, if any. */
export const getActiveSal125Subscription = (
  user: UserType | undefined
) => {
  return user?.subscriptions?.find(
    (sub) =>
      isSal125SubscriptionProduct(sub.product) &&
      sub.status === "active" &&
      isSubscriptionInActiveWindow(sub.startDate, sub.endDate)
  );
};

/** Resolve the active SAL125 subscription end date from auth user payload. */
export const getSal125SubscriptionEndDate = (
  user: UserType | undefined
): Date | null => {
  const fromSummary = parseSubscriptionDate(user?.subscription?.endDate);
  if (fromSummary) return fromSummary;

  return parseSubscriptionDate(getActiveSal125Subscription(user)?.endDate);
};

export const getRemainingSubscriptionMs = (
  user: UserType | undefined
): number => {
  const endDate = getSal125SubscriptionEndDate(user);
  if (!endDate) return 0;
  return Math.max(0, endDate.getTime() - Date.now());
};

const calculateDaysBetween = (from: Date, to: Date = new Date()): number => {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.ceil(Math.abs(to.getTime() - from.getTime()) / msPerDay);
};

const isTrialActiveChecker = (startDate?: Date): boolean => {
  if (!startDate || isNaN(startDate.getTime())) return false;
  return calculateDaysBetween(startDate) < TRIAL_DURATION_DAYS;
};

export const useFreeTrial = (numberOfCreatedItems: number) => {
  const user = useSelector((state: RootState) => state.auth.user);

  const result = useMemo(() => {
    const subscription = getActiveSal125Subscription(user);
    const isTrialUser = subscription?.type === "free_trial";
    const activatedAt = subscription?.startDate
      ? new Date(subscription.startDate)
      : undefined;
    const isTrialActive = isTrialActiveChecker(activatedAt);
    const hasPermission = !isTrialUser
      ? true
      : isTrialActive && numberOfCreatedItems < MAX_ITEMS_ALLOWED_DURING_TRIAL;

    return {
      isTrialUser,
      isTrialActive,
      activatedAt,
      hasPermission,
    };
  }, [user, numberOfCreatedItems]);

  return result;
};
